/** Инструктаж (КЧ-06) и журнал инструктажей по форме */
import {
  BRIEFING_KINDS,
  BRIEFING_REASONS,
  type BriefingDetail,
  type BriefingDueRow,
  type BriefingJournalRow,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { Printer } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { useSafetyWorkers } from './safetyShared';

export function BriefingPage() {
  const { briefingId } = useParams({ strict: false }) as { briefingId: string };
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['qa-briefing', briefingId],
    queryFn: () => api<BriefingDetail>(`/quality/briefings/${briefingId}`),
  });
  useCrumbs(q.data ? [{ label: q.data.number, title: q.data.topic }] : null, q.error);
  const refresh = (d: BriefingDetail) => {
    qc.setQueryData(['qa-briefing', briefingId], d);
    qc.invalidateQueries({ queryKey: ['qa-briefings'] });
    qc.invalidateQueries({ queryKey: ['inbox'] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
  };
  const act = useMutation({
    mutationFn: (a: { path: string; body?: unknown; method?: 'POST' | 'DELETE'; done?: string }) =>
      api<BriefingDetail>(`/quality/briefings/${briefingId}/${a.path}`, {
        method: a.method ?? 'POST',
        body: a.method === 'DELETE' ? undefined : (a.body ?? {}),
      }).then((d) => ({ d, done: a.done })),
    onSuccess: ({ d, done }) => {
      refresh(d);
      if (done) toast(done);
    },
  });
  const [worker, setWorker] = useState('');
  const workers = useSafetyWorkers(q.data?.projectId);
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const d = q.data;
  const inList = new Set(d.attendeeList.map((a) => a.workerId));
  return (
    <Page>
      <PageHeader
        title={`${d.number}: ${d.topic}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={d.status === 'closed' ? 'neutral' : 'info'}>
              {d.status === 'closed' ? 'Журнал закрыт' : 'Идёт сбор подписей'}
            </Badge>
            <span>{BRIEFING_KINDS[d.kind]}</span>
          </span>
        }
        actions={
          d.can.close ? (
            <Button
              variant="primary"
              size="lg"
              loading={act.isPending}
              onClick={() => act.mutate({ path: 'close', done: 'Журнал закрыт' })}
            >
              Закрыть журнал
            </Button>
          ) : null
        }
      />
      {act.isError ? <ErrorBox error={act.error} className="mb-4" /> : null}
      <Panel className="mb-6">
        <KeyValue
          items={[
            {
              label: 'Проект',
              value: (
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: d.projectId }}
                  className="inline-flex min-h-11 items-center underline md:min-h-0"
                >
                  {d.projectCode}
                </Link>
              ),
            },
            { label: 'Дата проведения', value: date(d.heldOn) },
            { label: 'Проводил', value: d.conductorName },
            ...(d.reason ? [{ label: 'Основание', value: BRIEFING_REASONS[d.reason] }] : []),
            ...(d.place ? [{ label: 'Место', value: d.place }] : []),
            ...(d.permitId
              ? [
                  {
                    label: 'Наряд-допуск',
                    value: (
                      <Link
                        to="/qhse/permits/$permitId"
                        params={{ permitId: d.permitId }}
                        className="inline-flex min-h-11 items-center underline md:min-h-0"
                      >
                        {d.permitNumber}
                      </Link>
                    ),
                  },
                ]
              : []),
            ...(d.nextDueOn ? [{ label: 'Повторный не позже', value: date(d.nextDueOn) }] : []),
          ]}
        />
      </Panel>
      <Panel title={`Подписи (${d.signed} из ${d.attendees})`}>
        {d.can.edit ? (
          <div className="mb-4 flex flex-col gap-2 md:flex-row">
            <Select
              aria-label="Работник"
              className="h-11 md:h-10 md:w-96"
              value={worker}
              onChange={(e) => setWorker(e.target.value)}
            >
              <option value="">Добавить в список…</option>
              {(workers.data ?? [])
                .filter((w) => !inList.has(w.id))
                .map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.fullName}
                  </option>
                ))}
            </Select>
            <Button
              variant="secondary"
              className="h-11 md:h-10"
              disabled={!worker}
              onClick={() => {
                act.mutate({ path: 'attendees', body: { workerId: worker } });
                setWorker('');
              }}
            >
              Добавить
            </Button>
          </div>
        ) : null}
        <ul className="flex flex-col gap-2">
          {d.attendeeList.map((a) => (
            <li
              key={a.workerId}
              className="flex flex-col gap-2 rounded-md border border-line p-3 md:flex-row md:items-center"
            >
              <span className="min-w-0 flex-1 text-[14px] text-ink [overflow-wrap:anywhere]">
                {a.fullName}
                {a.counterpartyName ? ` · ${a.counterpartyName}` : ''}
              </span>
              <span className="flex flex-wrap items-center gap-2">
                {a.signedAt ? (
                  <Badge tone="success">
                    Подписал{a.method === 'paper' ? ' на бумаге' : ' в приложении'} · {dateTime(a.signedAt)}
                  </Badge>
                ) : d.status === 'open' ? (
                  <>
                    <Badge tone="warning">Не подписал</Badge>
                    {a.isMe ? (
                      <Button
                        className="h-11 md:h-10"
                        variant="primary"
                        loading={act.isPending}
                        onClick={() => act.mutate({ path: 'sign', done: 'Подпись принята' })}
                      >
                        Ознакомлен, подписываю
                      </Button>
                    ) : d.can.signAnyone ? (
                      <Button
                        className="h-11 md:h-10"
                        variant="secondary"
                        loading={act.isPending}
                        onClick={() =>
                          act.mutate({
                            path: 'sign',
                            body: { workerId: a.workerId },
                            done: 'Бумажная подпись отмечена',
                          })
                        }
                      >
                        Подписал на бумаге
                      </Button>
                    ) : null}
                    {d.can.edit ? (
                      <button
                        type="button"
                        className="inline-flex min-h-11 items-center px-2 text-[13px] text-danger hover:underline md:min-h-0"
                        onClick={() =>
                          act.mutate({
                            path: `attendees/${a.workerId}`,
                            method: 'DELETE',
                            done: 'Убран из списка',
                          })
                        }
                      >
                        Не явился
                      </button>
                    ) : null}
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    </Page>
  );
}

/** Журнал инструктажей по форме: печатается со страницы; ниже — кому пора на повторный */
export function BriefingJournalPage() {
  const search = useSearch({ strict: false }) as { project?: string };
  useCrumbs([{ label: 'Журнал инструктажей' }]);
  const q = useQuery({
    queryKey: ['qa-journal', search.project],
    queryFn: () =>
      api<BriefingJournalRow[]>('/quality/briefings/journal', { query: { projectId: search.project } }),
    enabled: Boolean(search.project),
  });
  const due = useQuery({
    queryKey: ['qa-due', search.project],
    queryFn: () => api<BriefingDueRow[]>('/quality/briefings/due', { query: { projectId: search.project } }),
    enabled: Boolean(search.project),
  });
  return (
    <Page>
      <PageHeader
        title="Журнал инструктажей"
        subtitle="Одна строка — один работник: дата, вид, тема, кто проводил, подпись."
        actions={
          <Button
            variant="secondary"
            size="lg"
            icon={<Printer className="size-4" />}
            onClick={() => window.print()}
          >
            Печать
          </Button>
        }
      />
      {!search.project ? <ErrorBox error={new Error('Выберите проект на вкладке «Инструктажи»')} /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {q.isPending && search.project ? <Spinner /> : null}
      {q.data ? (
        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="w-full min-w-[720px] text-left text-[13px]">
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                {['№', 'Дата', 'Вид', 'Тема', 'Работник', 'Проводил', 'Подпись'].map((h) => (
                  <th key={h} className="px-3 py-2 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => (
                <tr key={`${r.number}-${r.workerName}`} className="border-t border-line align-top">
                  <td className="num px-3 py-2">{r.number}</td>
                  <td className="px-3 py-2">{date(r.heldOn)}</td>
                  <td className="px-3 py-2">
                    {BRIEFING_KINDS[r.kind]}
                    {r.reason ? ` (${BRIEFING_REASONS[r.reason]})` : ''}
                  </td>
                  <td className="px-3 py-2">{r.topic}</td>
                  <td className="px-3 py-2">
                    {r.workerName}
                    {r.counterpartyName ? ` · ${r.counterpartyName}` : ''}
                  </td>
                  <td className="px-3 py-2">{r.conductorName}</td>
                  <td className="px-3 py-2">
                    {r.signedAt
                      ? `${dateTime(r.signedAt)}${r.method === 'paper' ? ', бумага' : ''}`
                      : 'не подписал'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {due.data?.length ? (
        <Panel title="Пора на повторный инструктаж" className="mt-6 print:hidden">
          <ul className="flex flex-col gap-1 text-[14px]">
            {due.data.map((r) => (
              <li key={r.workerId} className="[overflow-wrap:anywhere]">
                {r.fullName}: {r.lastOn ? `последний ${date(r.lastOn)}, ` : 'ни разу не проходил, '}
                срок {date(r.dueOn)} {r.overdue ? <Badge tone="danger">просрочен</Badge> : null}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </Page>
  );
}
